// src/modules/config/components/MenuItemModal/index.tsx - ПОЛНАЯ ВЕРСИЯ с исправлениями

import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Button, Space, Tabs, message } from 'antd';
import { MenuItem, MenuCategory } from '@/types';
import { BasicInfoTab } from './BasicInfoTab';
import { PricingTab } from './PricingTab';
import { RecipesTab } from './RecipesTab';
import { ImagesAndQRTab } from './ImagesAndQRTab';
import { SEOTab } from './SEOTab';

interface MenuItemModalProps {
  visible: boolean;
  menuItem: MenuItem | null;
  categories: MenuCategory[];
  loading: boolean;
  siteBaseUrl?: string;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  onGenerateQR?: (itemId: number) => void;
  onRemoveQR?: (itemId: number) => void;
}

export const MenuItemModal: React.FC<MenuItemModalProps> = ({
  visible, menuItem, categories, loading, siteBaseUrl, onCancel, onSubmit, onGenerateQR, onRemoveQR
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const [additionalImages, setAdditionalImages] = useState<any[]>([]);
  const [existingDetailedImages, setExistingDetailedImages] = useState<string[]>([]);
  const [imagesToRemove, setImagesToRemove] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState('main');
  const [hasChanges, setHasChanges] = useState(false);
  const [initialValues, setInitialValues] = useState<any>({});
  
  const isEdit = !!menuItem;

  const generateSlug = (name: string) => name.toLowerCase().trim()
    .replace(/[а-я]/g, c => ({'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ё':'yo','ж':'zh','з':'z','и':'i','й':'y','к':'k','л':'l','м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u','ф':'f','х':'h','ц':'ts','ч':'ch','ш':'sh','щ':'sch','ъ':'','ы':'y','ь':'','э':'e','ю':'yu','я':'ya'})[c] || c)
    .replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '-').replace(/^-+|-+$/g, '');

  const generateArticle = () => `ART-${Math.floor(10000 + Math.random() * 90000)}`;

  // Функция для проверки изменений в форме
  const checkForChanges = useCallback(() => {
    if (!isEdit) {
      // Для создания нового блюда кнопка всегда активна
      setHasChanges(true);
      return;
    }

    const currentValues = form.getFieldsValue();
    
    // Получаем все ключи для сравнения (объединяем ключи из обеих объектов)
    const allKeys = new Set([
      ...Object.keys(initialValues),
      ...Object.keys(currentValues)
    ]);
    
    // Проверяем изменения в полях формы
    const formFieldsChanged = Array.from(allKeys).some(key => {
      const initial = initialValues[key];
      const current = currentValues[key];
      
      // Обрабатываем undefined/null как пустые строки для текстовых полей
      const normalizeValue = (value: any) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string') return value.trim();
        return value;
      };
      
      const normalizedInitial = normalizeValue(initial);
      const normalizedCurrent = normalizeValue(current);
      
      // Специальная обработка для массивов (категории)
      if (Array.isArray(normalizedInitial) && Array.isArray(normalizedCurrent)) {
        const sortedInitial = [...normalizedInitial].sort();
        const sortedCurrent = [...normalizedCurrent].sort();
        return JSON.stringify(sortedInitial) !== JSON.stringify(sortedCurrent);
      }
      
      // Обычное сравнение
      return normalizedInitial !== normalizedCurrent;
    });

    // Проверяем изменения в изображениях
    const imagesChanged = fileList.length > 0 || 
                         additionalImages.length > 0 || 
                         imagesToRemove.length > 0;

    const hasAnyChanges = formFieldsChanged || imagesChanged;
    
    console.log('Change detection:', {
      formFieldsChanged,
      imagesChanged,
      hasAnyChanges,
      currentValues,
      initialValues
    });

    setHasChanges(hasAnyChanges);
  }, [isEdit, initialValues, form, fileList, additionalImages, imagesToRemove]);

  useEffect(() => {
    if (visible) {
      if (menuItem) {
        console.log('Initializing form with menuItem:', menuItem);
        console.log('MenuItem categories:', menuItem.category, typeof menuItem.category);
        
        const formValues = {
          name: menuItem.name, 
          name_kz: menuItem.name_kz || '', 
          name_en: menuItem.name_en || '',     
          

          slug: menuItem.slug,
          article: menuItem.article, 
          
          // ИСПРАВЛЕНО: Убеждаемся что category - это массив чисел
          category: Array.isArray(menuItem.category) 
            ? menuItem.category 
            : (menuItem.category ? [menuItem.category] : []),
            
          description: menuItem.description || '', 
          description_kz: menuItem.description_kz || '',
          description_en: menuItem.description_en || '',
          composition: menuItem.composition || '', 
          composition_kz: menuItem.composition_kz || '',
          composition_en: menuItem.composition_en || '',
          recipe: menuItem.recipe || '', 
          recipe_kz: menuItem.recipe_kz || '',
          recipe_en: menuItem.recipe_en || '',
          storage_instructions: menuItem.storage_instructions || '',
          storage_instructions_kz: menuItem.storage_instructions_kz || '',
          storage_instructions_en: menuItem.storage_instructions_en || '',
          cooking_instructions: menuItem.cooking_instructions || '',
          cooking_instructions_kz: menuItem.cooking_instructions_kz || '',
          cooking_instructions_en: menuItem.cooking_instructions_en || '',
          price: menuItem.price, 
          measure_value: menuItem.measure_value, 
          measure_unit: menuItem.measure_unit,
          calories: menuItem.calories || 0, 
          proteins: menuItem.proteins || 0,
          fats: menuItem.fats || 0, 
          carbs: menuItem.carbs || 0,
          current_stock: menuItem.current_stock || 0, 
          total_manufactured: menuItem.total_manufactured || 0,
          completed_quantity: menuItem.completed_quantity || 0,
          cart_reserved_quantity: menuItem.cart_reserved_quantity || 0,
          pending_quantity: menuItem.pending_quantity || 0,
          confirmed_quantity: menuItem.confirmed_quantity || 0,
          preparing_quantity: menuItem.preparing_quantity || 0,
          delivering_quantity: menuItem.delivering_quantity || 0,
          status: menuItem.status || 'active', 
          meta_title: menuItem.meta_title || '',
          meta_title_kz: menuItem.meta_title_kz || '', 
          meta_title_en: menuItem.meta_title_en || '',
          meta_description: menuItem.meta_description || '',
          meta_description_kz: menuItem.meta_description_kz || '',
          meta_description_en: menuItem.meta_description_en || ''
        };
        
        console.log('Setting form values:', formValues);
        console.log('Categories from formValues:', formValues.category);
        
        form.setFieldsValue(formValues);
        setInitialValues(formValues);

        // Инициализация изображений
        setFileList([]);
        setAdditionalImages([]);
        
        const existingImages = menuItem.detailed_images_urls || [];
        console.log('Setting existing detailed images:', existingImages);
        setExistingDetailedImages(existingImages);
        setImagesToRemove([]);
        
        // Изначально нет изменений
        setHasChanges(false);
        
      } else {
        console.log('Creating new menu item');
        const defaultValues = {
          article: generateArticle(), 
          status: 'active',
          current_stock: 0, 
          total_manufactured: 0, 
          calories: 0, 
          proteins: 0, 
          fats: 0, 
          carbs: 0,
          category: [] // ИСПРАВЛЕНО: Пустой массив для новых блюд
        };
        
        form.resetFields();
        form.setFieldsValue(defaultValues);
        setInitialValues({});
        
        setFileList([]);
        setAdditionalImages([]);
        setExistingDetailedImages([]);
        setImagesToRemove([]);
        
        // Для создания нового блюда кнопка всегда активна
        setHasChanges(true);
      }
    }
  }, [visible, menuItem, form]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value && !isEdit) {
      form.setFieldsValue({ slug: generateSlug(e.target.value) });
    }
    // Проверяем изменения с небольшой задержкой, чтобы форма успела обновиться
    setTimeout(checkForChanges, 0);
  };

  const handleRemoveExistingDetailedImage = (imageIndex: number) => {
    console.log('Removing existing detailed image at index:', imageIndex);
    setImagesToRemove(prev => [...prev, imageIndex]);
    setExistingDetailedImages(prev => prev.filter((_, index) => index !== imageIndex));
    setTimeout(checkForChanges, 0);
  };

  const handleSubmit = async (values: any) => {
    try {
      const allFormValues = form.getFieldsValue();
      const completeValues = { ...allFormValues, ...values };
      
      console.log('Values from current tab:', values);
      console.log('All form values:', allFormValues);
      console.log('Complete merged values:', completeValues);
      
      if (!completeValues.name?.trim()) {
        message.error('Введите название блюда');
        return;
      }
      
      if (!completeValues.slug?.trim()) {
        completeValues.slug = generateSlug(completeValues.name);
      }
      if (!completeValues.article?.trim()) {
        completeValues.article = generateArticle();
      }
      
      const submitData = { ...completeValues };
      
      // Добавляем основное изображение если есть новое
      if (fileList.length > 0) {
        const newImage = fileList.find(file => file.originFileObj);
        if (newImage?.originFileObj) {
          submitData.image = newImage.originFileObj;
          console.log('New main image added:', newImage.originFileObj.name);
        }
      }
      
      // Добавляем новые дополнительные изображения если есть
      if (additionalImages.length > 0) {
        const newAdditionalImages = additionalImages
          .filter(img => img.originFileObj)
          .map(img => img.originFileObj);
        if (newAdditionalImages.length > 0) {
          submitData.additional_images = newAdditionalImages;
          console.log('New additional images:', newAdditionalImages.length);
        }
      }
      
      // Добавляем информацию об удаляемых изображениях
      if (imagesToRemove.length > 0) {
        submitData.images_to_remove = imagesToRemove;
        console.log('Images marked for removal:', imagesToRemove);
      }
      
      console.log('Final submit data:', submitData);
      
      await onSubmit(submitData);
      
      // Диалог закроется автоматически через родительский компонент
      // поэтому не нужно обновлять состояние здесь
      
    } catch (error) {
      console.error('Submit error:', error);
      message.error('Ошибка при сохранении блюда');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setAdditionalImages([]);
    setExistingDetailedImages([]);
    setImagesToRemove([]);
    setActiveTab('main');
    setHasChanges(false);
    setInitialValues({});
    onCancel();
  };

  const beforeUpload = (file: any) => {
    if (!file.type.startsWith('image/')) { 
      message.error('Можно загружать только изображения!'); 
      return false; 
    }
    if (file.size / 1024 / 1024 > 5) { 
      message.error('Размер изображения должен быть меньше 5MB!'); 
      return false; 
    }
    return false;
  };

  const handleMainImageChange = ({ fileList: newFileList }: any) => {
    console.log('Main image change:', newFileList);
    setFileList(newFileList);
    setTimeout(checkForChanges, 0);
  };

  const handleAdditionalImagesChange = ({ fileList: newFileList }: any) => {
    console.log('Additional images change:', newFileList);
    setAdditionalImages(newFileList);
    setTimeout(checkForChanges, 0);
  };

  const tabItems = [
    { 
      key: 'main', 
      label: 'Основная информация', 
      children: <BasicInfoTab categories={categories} onNameChange={handleNameChange} /> 
    },
    { 
      key: 'pricing', 
      label: 'Цена и характеристики', 
      children: <PricingTab /> 
    },
    { 
      key: 'recipes', 
      label: 'Рецепты и инструкции', 
      children: <RecipesTab isEdit={isEdit} menuItem={menuItem} /> 
    },
    { 
      key: 'images', 
      label: 'Изображения и QR', 
      children: (
        <ImagesAndQRTab 
          fileList={fileList} 
          additionalImages={additionalImages}
          existingDetailedImages={existingDetailedImages}
          menuItem={menuItem} 
          loading={loading} 
          onMainImageChange={handleMainImageChange}
          onAdditionalImagesChange={handleAdditionalImagesChange}
          onRemoveExistingDetailedImage={handleRemoveExistingDetailedImage}
          beforeUpload={beforeUpload} 
          onGenerateQR={onGenerateQR} 
          onRemoveQR={onRemoveQR} 
        />
      )
    },
    { 
      key: 'seo', 
      label: 'SEO', 
      children: <SEOTab form={form} siteBaseUrl={siteBaseUrl} /> 
    }
  ];

  return (
    <Modal 
      title={isEdit ? "Редактировать блюдо" : "Создать блюдо"} 
      open={visible} 
      onCancel={handleCancel} 
      footer={null} 
      width={1200} 
      destroyOnHidden
      maskClosable={false}
      keyboard={false}
    >
      <Form 
        form={form} 
        layout="vertical" 
        onFinish={handleSubmit}
        onValuesChange={checkForChanges}
        scrollToFirstError
      >
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={tabItems}
        />
        <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
          <Space>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading} 
              size="large"
              disabled={loading || !hasChanges}
            >
              {isEdit ? 'Обновить блюдо' : 'Создать блюдо'}
            </Button>
            <Button onClick={handleCancel} size="large" disabled={loading}>
              Отмена
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};